import { Clipboard } from '@angular/cdk/clipboard';
import { Component, computed, DestroyRef, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyBadgeModule, BadgeColor } from '@vendasta/galaxy/badge';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { interval, take, takeWhile } from 'rxjs';
import { SMSI18NModule } from '../../../assets/i18n/sms-i18n.module';
import { A2pAnalysisService } from './a2p-analysis.service';
import { A2pAnalysis, A2pAnalysisItem, A2pAnalysisSeverity, SEVERITY_ORDER } from './a2p-analysis.types';

const SEVERITY_BADGE_COLOR: Record<A2pAnalysisSeverity, BadgeColor> = {
  must_fix: 'red',
  recommended: 'yellow',
  other: 'grey',
};

const LOADING_STEPS = [
  'SMS_REGISTRATION.V2.ANALYSIS.LOADING.STEP_1',
  'SMS_REGISTRATION.V2.ANALYSIS.LOADING.STEP_2',
  'SMS_REGISTRATION.V2.ANALYSIS.LOADING.STEP_3',
  'SMS_REGISTRATION.V2.ANALYSIS.LOADING.STEP_4',
];
const LOADING_STEP_INTERVAL_MS = 1200;

interface RawFormProvider {
  getRawValue(): Record<string, unknown>;
}

@Component({
  selector: 'sms-a2p-analysis',
  imports: [
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    TranslateModule,
    SMSI18NModule,
    GalaxyAlertModule,
    GalaxyBadgeModule,
  ],
  templateUrl: './a2p-analysis.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './a2p-analysis.component.scss',
})
export class A2pAnalysisComponent {
  readonly form = input.required<RawFormProvider>();

  private readonly analysisService = inject(A2pAnalysisService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translateService = inject(TranslateService);
  private readonly clipboard = inject(Clipboard);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly loading = signal(false);
  protected readonly error = signal(false);
  protected readonly result = signal<A2pAnalysis | null>(null);

  protected readonly loadingSteps = LOADING_STEPS;
  protected readonly loadingStep = signal(0);

  protected readonly sortedItems = computed(() =>
    [...(this.result()?.items ?? [])].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]),
  );

  protected readonly counts = computed(() => {
    const items = this.result()?.items ?? [];
    return {
      must_fix: items.filter((item) => item.severity === 'must_fix').length,
      recommended: items.filter((item) => item.severity === 'recommended').length,
      other: items.filter((item) => item.severity === 'other').length,
    };
  });

  analyse(): void {
    if (this.loading()) return;
    this.loading.set(true);
    this.error.set(false);
    this.startLoadingAnimation();

    this.analysisService
      .analyse(this.form().getRawValue())
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (analysis) => {
          this.result.set(analysis);
          this.loading.set(false);
        },
        error: () => {
          this.result.set(null);
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }

  private startLoadingAnimation(): void {
    this.loadingStep.set(0);
    interval(LOADING_STEP_INTERVAL_MS)
      .pipe(
        take(this.loadingSteps.length - 1),
        takeWhile(() => this.loading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.loadingStep.update((step) => step + 1));
  }

  badgeColor(severity: A2pAnalysisSeverity): BadgeColor {
    return SEVERITY_BADGE_COLOR[severity];
  }

  severityLabelKey(severity: A2pAnalysisSeverity): string {
    return `SMS_REGISTRATION.V2.ANALYSIS.SEVERITY.${severity.toUpperCase()}`;
  }

  copyExample(item: A2pAnalysisItem): void {
    if (!item.exampleWording) return;
    this.clipboard.copy(item.exampleWording);
    this.snackbarService.openSuccessSnack(this.translateService.instant('SMS_REGISTRATION.V2.ANALYSIS.COPIED'));
  }
}
